/**
 * Automations View — Scheduled tasks / cron job management.
 *
 * Schedule picker: "tappable sentence" with dropdown popovers.
 * The schedule is shown as a single readable sentence with highlighted tokens.
 * Click a token to open a compact dropdown. Pick an option. Done in 2 clicks.
 *
 *   Runs  [every day ▾]  at  [9 AM ▾]
 *
 * Converts to cron internally.
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  getServices,
  type AutomationAction,
  type AutomationInfo,
  type AutomationRun,
  type ChannelInfo,
} from "../services/index.ts";
import { TIERS, getModelForTier, getModelById, MODEL_CATALOG, type ModelTier } from "../models.ts";

/** Reverse-lookup: find which tier would produce the given model ID */
function getTierForModel(modelId: string): ModelTier {
  // First check if any tier's active preference matches
  for (const t of TIERS) {
    if (getModelForTier(t.id) === modelId) return t.id;
  }
  // Fall back to the model's catalog tier
  const entry = MODEL_CATALOG.find((m) => m.id === modelId);
  if (entry) return entry.tier;
  return "normal";
}

// ── Day / time options ──

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

type DayOption = { dow: string; dom: string; label: string; group: string };

const DAY_OPTIONS: DayOption[] = [
  { dow: "*", dom: "*", label: "Every day", group: "Frequency" },
  { dow: "1-5", dom: "*", label: "Weekdays", group: "Frequency" },
  ...WEEKDAY_NAMES.map((name, i) => ({ dow: String(i), dom: "*", label: name, group: "Weekly" })),
  ...Array.from({ length: 28 }, (_, i) => ({
    dow: "*",
    dom: String(i + 1),
    label: `${ordinal(i + 1)}`,
    group: "Monthly",
  })),
];

// Time options — every half hour, 12h format
type TimeOption = { hour: number; minute: number; label: string };

const TIME_OPTIONS: TimeOption[] = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = (i % 2) * 30;
  return { hour, minute, label: fmtTime(hour, minute) };
});

// ── Cron helpers ──

type ParsedCron = { hour: number; minute: number; dow: string; dom: string };

function parseCron(cron: string): ParsedCron {
  const [min, hour, dom, , dow] = cron.split(" ");
  return {
    hour: hour === "*" ? 9 : parseInt(hour),
    minute: min.startsWith("*/") ? 0 : min === "*" ? 0 : parseInt(min),
    dow,
    dom,
  };
}

function buildCron(p: ParsedCron): string {
  if (p.dow === "*" && p.dom === "*") return `${p.minute} ${p.hour} * * *`;
  if (p.dom !== "*") return `${p.minute} ${p.hour} ${p.dom} * *`;
  return `${p.minute} ${p.hour} * * ${p.dow}`;
}

function cronToDescription(cron: string): string {
  const parts = cron.split(" ");
  if (parts.length !== 5) return cron;
  const [min, hour, dom, , dow] = parts;

  if (min.startsWith("*/")) {
    const n = parseInt(min.slice(2));
    return n === 1 ? "Every minute" : `Every ${n} minutes`;
  }
  if (hour === "*" && min !== "*") return `Every hour at :${min.padStart(2, "0")}`;
  if (hour === "*") return "Every hour";

  const t = fmtTime(parseInt(hour), parseInt(min));
  const dayOpt = DAY_OPTIONS.find((o) => o.dow === dow && o.dom === dom);
  const dayLabel = dayOpt ? getDayDisplayLabel(dayOpt) : "every day";

  return `Runs ${dayLabel} at ${t}`;
}

/** Short display for the token: "every day", "weekdays", "Mondays", "the 5th" */
function getDayDisplayLabel(opt: DayOption): string {
  if (opt.group === "Frequency") return opt.label.toLowerCase();
  if (opt.group === "Weekly") return `every ${opt.label}`;
  return `the ${opt.label} of each month`;
}

function fmtTime(h: number, m: number): string {
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12} ${suffix}` : `${h12}:${m.toString().padStart(2, "0")} ${suffix}`;
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function getDayIndex(p: ParsedCron): number {
  const idx = DAY_OPTIONS.findIndex((o) => o.dow === p.dow && o.dom === p.dom);
  return idx >= 0 ? idx : 0;
}

function getDayTokenLabel(p: ParsedCron): string {
  const opt = DAY_OPTIONS[getDayIndex(p)];
  return opt ? getDayDisplayLabel(opt) : "every day";
}

// ── Dropdown type ──

type OpenDropdown = "day" | "time" | null;

// ══════════════════════════════════════════════════════════

@customElement("katanoid-automations-view")
export class KatanoidAutomationsView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @state() private automations: AutomationInfo[] = [];
  @state() private loading = true;

  // Create / Edit form
  @state() private showCreateForm = false;
  @state() private editingId: string | null = null;
  @state() private formName = "";
  @state() private formCron = "0 9 * * *"; // default: every day at 9 AM
  @state() private formAction = "";
  @state() private formDeliverToChannel = ""; // "" = chat only
  @state() private formModel: ModelTier = "normal";
  @state() private creating = false;

  // Delivery target options
  @state() private connectedChannels: ChannelInfo[] = [];

  // Detail / expanded view
  @state() private detailAutomation: AutomationInfo | null = null;
  @state() private detailRuns: AutomationRun[] = [];
  @state() private detailLoading = false;

  // Schedule dropdowns
  @state() private openDropdown: OpenDropdown = null;

  private boundCloseDropdown = this.closeDropdownOutside.bind(this);

  override connectedCallback(): void {
    super.connectedCallback();
    this.loadAutomations();
    this.loadChannels();
    document.addEventListener("click", this.boundCloseDropdown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this.boundCloseDropdown);
  }

  private closeDropdownOutside(e: MouseEvent): void {
    if (this.openDropdown === null) return;
    const target = e.target as HTMLElement;
    if (target.closest(".sched-dropdown-anchor")) return;
    this.openDropdown = null;
  }

  private async loadAutomations(): Promise<void> {
    this.loading = true;
    try {
      const { automations } = getServices();
      this.automations = await automations.listAutomations();
    } catch {
      this.automations = [];
    }
    this.loading = false;
  }

  private async loadChannels(): Promise<void> {
    try {
      const { channels } = getServices();
      const all = await channels.listChannels();
      this.connectedChannels = all.filter((c) => c.status === "connected");
    } catch {
      this.connectedChannels = [];
    }
  }

  private async openDetail(a: AutomationInfo): Promise<void> {
    this.detailAutomation = a;
    this.detailLoading = true;
    try {
      const { automations } = getServices();
      this.detailRuns = await automations.getRunHistory(a.id);
    } catch {
      this.detailRuns = [];
    }
    this.detailLoading = false;
  }

  private closeDetail(): void {
    this.detailAutomation = null;
    this.detailRuns = [];
  }

  // ── Main render ──

  protected override render(): TemplateResult {
    // Show detail view if an automation is selected
    if (this.detailAutomation) {
      return this.renderDetailView(this.detailAutomation);
    }

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <h1 class="view-title">Automations</h1>
          <button
            class="btn btn--primary btn--sm"
            @click=${() => {
              this.showCreateForm = !this.showCreateForm;
              if (this.showCreateForm) this.resetForm();
              this.editingId = null;
            }}
          >
            ${this.showCreateForm ? "Cancel" : "+ New Automation"}
          </button>
        </div>

        ${this.showCreateForm ? this.renderCreateForm() : nothing}

        ${this.loading
          ? html`
              <div class="automations-list">
                <div class="shimmer shimmer--card"></div>
                <div class="shimmer shimmer--card"></div>
              </div>
            `
          : this.automations.length === 0
            ? this.renderEmptyState()
            : this.renderAutomationsList()}
      </div>
    `;
  }

  private renderEmptyState(): TemplateResult {
    return html`
      <div class="sessions-empty">
        <div class="sessions-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <h2 class="sessions-empty-title">No automations yet</h2>
        <p class="sessions-empty-desc">
          Schedule recurring tasks \u2014 daily briefings, weekly summaries,
          status checks, and more.
        </p>
        <button class="btn btn--primary" @click=${() => { this.showCreateForm = true; this.resetForm(); }}>
          Create your first automation
        </button>
      </div>
    `;
  }

  // ── Create Form ──

  private renderCreateForm(): TemplateResult {
    const p = parseCron(this.formCron);

    return html`
      <div class="automation-form card">
        <div class="automation-form-title">${this.editingId ? "Edit Automation" : "New Automation"}</div>

        <!-- Name -->
        <div class="automation-form-field">
          <label class="input-label">Name</label>
          <input
            class="input"
            placeholder="e.g. Morning briefing"
            .value=${this.formName}
            @input=${(e: InputEvent) => { this.formName = (e.target as HTMLInputElement).value; }}
          />
        </div>

        <!-- Schedule: tappable sentence with dropdown popovers -->
        <div class="automation-form-field">
          <label class="input-label">Schedule</label>
          <div class="sched-sentence">
            <span class="sched-sentence-text">Runs</span>

            <div class="sched-dropdown-anchor">
              <button
                class="sched-token ${this.openDropdown === "day" ? "sched-token--open" : ""}"
                @click=${(e: MouseEvent) => { e.stopPropagation(); this.openDropdown = this.openDropdown === "day" ? null : "day"; }}
              >
                ${getDayTokenLabel(p)}
                <svg class="sched-token-chevron" width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 3.5L5 6.5L7.5 3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              ${this.openDropdown === "day" ? this.renderDayDropdown(p) : nothing}
            </div>

            <span class="sched-sentence-text">at</span>

            <div class="sched-dropdown-anchor">
              <button
                class="sched-token ${this.openDropdown === "time" ? "sched-token--open" : ""}"
                @click=${(e: MouseEvent) => { e.stopPropagation(); this.openDropdown = this.openDropdown === "time" ? null : "time"; }}
              >
                ${fmtTime(p.hour, p.minute)}
                <svg class="sched-token-chevron" width="10" height="10" viewBox="0 0 10 10"><path d="M2.5 3.5L5 6.5L7.5 3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              ${this.openDropdown === "time" ? this.renderTimeDropdown(p) : nothing}
            </div>
          </div>
        </div>

        <!-- Action -->
        <div class="automation-form-field">
          <label class="input-label">Task</label>
          <textarea
            class="input automation-form-textarea"
            placeholder="e.g. Send me a summary of today's news and my calendar"
            .value=${this.formAction}
            @input=${(e: InputEvent) => { this.formAction = (e.target as HTMLTextAreaElement).value; }}
          ></textarea>
        </div>

        <!-- Model tier -->
        <div class="automation-form-field">
          <label class="input-label">Model</label>
          <div class="automation-model-picker">
            ${TIERS.map((t) => {
              const modelId = getModelForTier(t.id);
              const model = getModelById(modelId);
              return html`
                <button
                  type="button"
                  class="automation-model-option ${t.id === this.formModel ? "automation-model-option--active" : ""}"
                  @click=${() => { this.formModel = t.id; }}
                >
                  <span class="automation-model-option-label">${t.label}</span>
                  <span class="automation-model-option-cost">${t.mult}</span>
                  <span class="automation-model-option-model">${model?.label ?? ""}</span>
                </button>
              `;
            })}
          </div>
        </div>

        <!-- Delivery target -->
        ${this.connectedChannels.length > 0
          ? html`
              <div class="automation-form-field">
                <label class="input-label">Deliver result to</label>
                <select
                  class="input"
                  .value=${this.formDeliverToChannel}
                  @change=${(e: Event) => {
                    this.formDeliverToChannel = (e.target as HTMLSelectElement).value;
                  }}
                >
                  <option value="">Chat only (in-app)</option>
                  ${this.connectedChannels.map(
                    (ch) => html`<option value=${ch.id}>${ch.displayName} (${ch.type})</option>`
                  )}
                </select>
                <div class="automation-form-hint">
                  Choose where the AI response is sent when this automation runs.
                </div>
                ${this.formDeliverToChannel && !this.connectedChannels.find((c) => c.id === this.formDeliverToChannel)?.hasDefaultChatId
                  ? html`<div class="automation-form-warning">
                      You haven't messaged this bot yet. Send it a message first so it knows where to deliver.
                    </div>`
                  : nothing}
              </div>
            `
          : nothing}

        <div class="automation-form-actions">
          <button
            class="btn btn--primary"
            ?disabled=${!this.formName.trim() || !this.formAction.trim() || this.creating}
            @click=${this.handleSubmit}
          >
            ${this.creating
              ? html`<div class="spinner spinner--sm"></div>`
              : this.editingId ? "Save Changes" : "Create Automation"}
          </button>
        </div>
      </div>
    `;
  }

  // ── Day dropdown ──

  private renderDayDropdown(p: ParsedCron): TemplateResult {
    const currentIdx = getDayIndex(p);
    const groups = [
      { label: "Frequency", items: DAY_OPTIONS.filter((o) => o.group === "Frequency") },
      { label: "Weekly", items: DAY_OPTIONS.filter((o) => o.group === "Weekly") },
      { label: "Monthly", items: DAY_OPTIONS.filter((o) => o.group === "Monthly") },
    ];

    return html`
      <div class="sched-dropdown sched-dropdown--day">
        ${groups.map((g) => html`
          <div class="sched-dropdown-group">
            <div class="sched-dropdown-group-label">${g.label}</div>
            <div class="sched-dropdown-items ${g.label === "Monthly" ? "sched-dropdown-items--grid" : ""}">
              ${g.items.map((opt) => {
                const idx = DAY_OPTIONS.indexOf(opt);
                const isActive = idx === currentIdx;
                return html`
                  <button
                    class="sched-dropdown-item ${isActive ? "sched-dropdown-item--active" : ""} ${g.label === "Monthly" ? "sched-dropdown-item--compact" : ""}"
                    @click=${(e: MouseEvent) => { e.stopPropagation(); this.selectDay(idx); }}
                  >
                    ${opt.label}
                  </button>
                `;
              })}
            </div>
          </div>
        `)}
      </div>
    `;
  }

  // ── Time dropdown ──

  private renderTimeDropdown(p: ParsedCron): TemplateResult {
    return html`
      <div class="sched-dropdown sched-dropdown--time">
        <div class="sched-dropdown-items sched-dropdown-items--time-grid">
          ${TIME_OPTIONS.map((opt) => {
            const isActive = opt.hour === p.hour && opt.minute === p.minute;
            return html`
              <button
                class="sched-dropdown-item sched-dropdown-item--compact ${isActive ? "sched-dropdown-item--active" : ""}"
                @click=${(e: MouseEvent) => { e.stopPropagation(); this.selectTime(opt.hour, opt.minute); }}
              >
                ${opt.label}
              </button>
            `;
          })}
        </div>
      </div>
    `;
  }

  // ── Selection handlers ──

  private selectDay(idx: number): void {
    const p = parseCron(this.formCron);
    const opt = DAY_OPTIONS[idx];
    p.dow = opt.dow;
    p.dom = opt.dom;
    this.formCron = buildCron(p);
    this.openDropdown = null;
  }

  private selectTime(hour: number, minute: number): void {
    const p = parseCron(this.formCron);
    p.hour = hour;
    p.minute = minute;
    this.formCron = buildCron(p);
    this.openDropdown = null;
  }

  // ── Automation cards ──

  private renderAutomationsList(): TemplateResult {
    return html`
      <div class="automations-list stagger-children">
        ${this.automations.map((a) => this.renderAutomationCard(a))}
      </div>
    `;
  }

  private renderAutomationCard(a: AutomationInfo): TemplateResult {
    const nextRun = a.nextRunAt ? this.fmtRelative(a.nextRunAt) : "Paused";
    const lastRun = a.lastRunAt ? this.fmtRelative(a.lastRunAt) : "Never";
    const deliveryChannel = a.action?.deliverTo
      ? this.connectedChannels.find((c) => c.id === a.action.deliverTo?.channelId)
      : null;

    return html`
      <div class="automation-card ${a.enabled ? "" : "automation-card--disabled"}">
        <div class="automation-card-header">
          <div class="automation-card-info automation-card-info--clickable" @click=${() => this.openDetail(a)}>
            <div class="automation-card-name">${a.name}</div>
            <div class="automation-card-schedule">${cronToDescription(a.schedule)}</div>
            ${deliveryChannel
              ? html`<div class="automation-card-delivery">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>
                  ${deliveryChannel.displayName}
                </div>`
              : nothing}
          </div>
          <button
            class="automation-toggle ${a.enabled ? "automation-toggle--on" : ""}"
            @click=${() => this.handleToggle(a)}
            title="${a.enabled ? "Disable" : "Enable"}"
          >
            <div class="automation-toggle-thumb"></div>
          </button>
        </div>

        <div class="automation-card-stats">
          <div class="automation-stat">
            <span class="automation-stat-label">Next run</span>
            <span class="automation-stat-value">${nextRun}</span>
          </div>
          <div class="automation-stat">
            <span class="automation-stat-label">Last run</span>
            <span class="automation-stat-value">${lastRun}</span>
          </div>
          <div class="automation-stat">
            <span class="automation-stat-label">Runs</span>
            <span class="automation-stat-value">${a.runCount}</span>
          </div>
        </div>

        ${a.lastError
          ? html`
              <div class="automation-error">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                ${a.lastError}
              </div>
            `
          : nothing}

        <div class="automation-card-actions">
          <button class="btn btn--ghost btn--sm" @click=${() => this.startEditing(a)}>
            Edit
          </button>
          <button class="btn btn--ghost btn--sm" @click=${() => this.openDetail(a)}>
            Details
          </button>
          <button class="btn btn--ghost btn--sm" @click=${() => this.handleRun(a)} ?disabled=${!a.enabled}>
            Run now
          </button>
          <button class="btn btn--ghost btn--sm session-card-delete" @click=${() => this.handleDelete(a.id)}>
            Delete
          </button>
        </div>
      </div>
    `;
  }

  // ── Form state ──

  private resetForm(): void {
    this.formName = "";
    this.formCron = "0 9 * * *";
    this.formAction = "";
    this.formDeliverToChannel = "";
    this.formModel = "normal";
    this.editingId = null;
  }

  private startEditing(a: AutomationInfo): void {
    this.editingId = a.id;
    this.showCreateForm = true;
    this.formName = a.name;
    this.formCron = a.schedule;
    this.formAction = a.action?.content ?? "";
    this.formDeliverToChannel = a.action?.deliverTo?.channelId ?? "";
    this.formModel = a.action?.model ? getTierForModel(a.action.model) : "normal";
  }

  // ── Handlers ──

  private handleSubmit = async (): Promise<void> => {
    if (!this.formName.trim() || !this.formAction.trim()) return;
    this.creating = true;
    try {
      const { automations } = getServices();
      const modelId = getModelForTier(this.formModel);
      const action: AutomationAction = {
        type: "message",
        content: this.formAction.trim(),
        model: modelId,
      };
      if (this.formDeliverToChannel) {
        action.deliverTo = { channelId: this.formDeliverToChannel };
      }

      if (this.editingId) {
        await automations.updateAutomation(this.editingId, {
          name: this.formName.trim(),
          schedule: this.formCron,
          action,
        });
      } else {
        await automations.createAutomation({
          name: this.formName.trim(),
          schedule: this.formCron,
          action,
        });
      }

      this.resetForm();
      this.showCreateForm = false;
      await this.loadAutomations();
    } catch { /* ignore */ }
    this.creating = false;
  };

  private handleToggle = async (a: AutomationInfo): Promise<void> => {
    try {
      const { automations } = getServices();
      await automations.toggleAutomation(a.id, !a.enabled);
      await this.loadAutomations();
    } catch { /* ignore */ }
  };

  private handleRun = async (a: AutomationInfo): Promise<void> => {
    try {
      const { automations } = getServices();
      await automations.runAutomation(a.id);
      await this.loadAutomations();
    } catch { /* ignore */ }
  };

  private handleDelete = async (id: string): Promise<void> => {
    try {
      const { automations } = getServices();
      await automations.deleteAutomation(id);
      this.automations = this.automations.filter((a) => a.id !== id);
    } catch { /* ignore */ }
  };

  // ── Detail view ──

  private renderDetailView(a: AutomationInfo): TemplateResult {
    const deliveryChannel = a.action?.deliverTo
      ? this.connectedChannels.find((c) => c.id === a.action.deliverTo?.channelId)
      : null;

    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <button class="btn btn--ghost btn--sm" @click=${() => this.closeDetail()}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
            Back
          </button>
          <h1 class="view-title" style="flex:1">${a.name}</h1>
          <button
            class="automation-toggle ${a.enabled ? "automation-toggle--on" : ""}"
            @click=${async () => { await this.handleToggle(a); this.detailAutomation = { ...a, enabled: !a.enabled }; }}
            title="${a.enabled ? "Disable" : "Enable"}"
          >
            <div class="automation-toggle-thumb"></div>
          </button>
        </div>

        <!-- Info card -->
        <div class="card automation-detail-card">
          <div class="automation-detail-section">
            <div class="automation-detail-label">Schedule</div>
            <div class="automation-detail-value">${cronToDescription(a.schedule)}</div>
          </div>
          <div class="automation-detail-section">
            <div class="automation-detail-label">Task</div>
            <div class="automation-detail-value automation-detail-task">${a.action?.content ?? "—"}</div>
          </div>
          <div class="automation-detail-section">
            <div class="automation-detail-label">Model</div>
            <div class="automation-detail-value">
              ${a.action?.model ? (getModelById(a.action.model)?.label ?? a.action.model) : "Default (Normal)"}
            </div>
          </div>
          <div class="automation-detail-section">
            <div class="automation-detail-label">Deliver to</div>
            <div class="automation-detail-value">
              ${deliveryChannel
                ? html`${deliveryChannel.displayName} (${deliveryChannel.type})`
                : "Chat only (in-app)"}
            </div>
          </div>
          <div class="automation-detail-stats">
            <div class="automation-stat">
              <span class="automation-stat-label">Total runs</span>
              <span class="automation-stat-value">${a.runCount}</span>
            </div>
            <div class="automation-stat">
              <span class="automation-stat-label">Next run</span>
              <span class="automation-stat-value">${a.nextRunAt ? this.fmtRelative(a.nextRunAt) : "Paused"}</span>
            </div>
            <div class="automation-stat">
              <span class="automation-stat-label">Last run</span>
              <span class="automation-stat-value">${a.lastRunAt ? this.fmtRelative(a.lastRunAt) : "Never"}</span>
            </div>
            <div class="automation-stat">
              <span class="automation-stat-label">Created</span>
              <span class="automation-stat-value">${new Date(a.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
            </div>
          </div>

          ${a.lastError
            ? html`
                <div class="automation-error">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  ${a.lastError}
                </div>
              `
            : nothing}

          <div class="automation-detail-actions">
            <button class="btn btn--primary btn--sm" @click=${() => this.handleRun(a)} ?disabled=${!a.enabled}>
              Run now
            </button>
            <button class="btn btn--ghost btn--sm session-card-delete" @click=${async () => { await this.handleDelete(a.id); this.closeDetail(); }}>
              Delete automation
            </button>
          </div>
        </div>

        <!-- Run history -->
        <div class="automation-history">
          <h2 class="automation-history-title">Run History</h2>
          ${this.detailLoading
            ? html`<div class="shimmer shimmer--card"></div>`
            : this.detailRuns.length === 0
              ? html`<div class="automation-history-empty">No runs yet. This automation hasn't been executed.</div>`
              : html`
                  <div class="automation-history-list">
                    ${this.detailRuns.map((run) => this.renderRunRow(run))}
                  </div>
                `}
        </div>
      </div>
    `;
  }

  private renderRunRow(run: AutomationRun): TemplateResult {
    const statusIcon = run.status === "success"
      ? html`<span class="automation-run-status automation-run-status--success">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </span>`
      : run.status === "error"
        ? html`<span class="automation-run-status automation-run-status--error">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </span>`
        : html`<span class="automation-run-status automation-run-status--running">
            <div class="spinner spinner--sm"></div>
          </span>`;

    const timestamp = new Date(run.startedAt).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
    const duration = run.completedAt
      ? this.fmtDuration(new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime())
      : "Running...";

    return html`
      <div class="automation-run-row">
        ${statusIcon}
        <div class="automation-run-info">
          <div class="automation-run-time">${timestamp}</div>
          ${run.resultSummary
            ? html`<div class="automation-run-summary">${run.resultSummary}</div>`
            : nothing}
          ${run.error
            ? html`<div class="automation-run-error">${run.error}</div>`
            : nothing}
        </div>
        <div class="automation-run-meta">
          <span class="automation-run-duration">${duration}</span>
          ${run.creditsUsed > 0
            ? html`<span class="automation-run-credits">${run.creditsUsed} cr</span>`
            : nothing}
        </div>
      </div>
    `;
  }

  // ── Formatting ──

  private fmtDuration(ms: number): string {
    if (ms < 1000) return "<1s";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return `${m}m ${s % 60}s`;
  }

  private fmtRelative(iso: string): string {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();

    if (diff > 0) {
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }

    const until = -diff;
    if (until < 3600000) return `in ${Math.floor(until / 60000)}m`;
    if (until < 86400000) return `in ${Math.floor(until / 3600000)}h`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-automations-view": KatanoidAutomationsView;
  }
}
